import { useState, useEffect, useRef } from 'react'
import { Loader2, Download, Github, Gitlab, FolderUp, Info } from 'lucide-react'
import {
  checkAppName,
  createImportFromURL,
  createImportFromGitHub,
  createImportFromGitLab,
  getGitHubLinkStatus,
  getGitLabLinkStatus,
  importEnvSecrets,
  type GitHubRepo,
  type GitLabRepo,
  type NameCheckResult,
} from '../lib/api'
import GitHubRepoBrowser from './GitHubRepoBrowser'
import GitLabRepoBrowser from './GitLabRepoBrowser'
import FolderDropZone from './FolderDropZone'
import { appSlugFromName, finalizeAppSlug, isValidAppSlug, normalizeAppSlugInput } from '../lib/appIdentity'

// =============================================================================
// URL Validation
// =============================================================================

const ALLOWED_HOSTS = ['github.com', 'gitlab.com', 'bitbucket.org']

function isValidImportURL(url: string): boolean {
  if (!url.startsWith('https://')) return false
  try {
    const parsed = new URL(url)
    const hostname = parsed.hostname.toLowerCase()
    if (ALLOWED_HOSTS.includes(hostname)) return true
    return hostname.includes('gitlab')
  } catch {
    return false
  }
}

// =============================================================================
// Name Extraction & Validation
// =============================================================================

function extractRepoName(url: string): string {
  try {
    const parsed = new URL(url)
    const lastSegment = parsed.pathname.split('/').filter(Boolean).pop() || ''
    return appSlugFromName(lastSegment.replace(/\.git$/, ''))
  } catch {
    return ''
  }
}

function extractDisplayName(url: string): string {
  try {
    const parsed = new URL(url)
    const lastSegment = parsed.pathname.split('/').filter(Boolean).pop() || ''
    return lastSegment.replace(/\.git$/, '')
  } catch {
    return ''
  }
}

// =============================================================================
// Component
// =============================================================================

type ImportSource = 'url' | 'github' | 'gitlab' | 'upload'

interface ImportProjectModalProps {
  onClose: () => void
  onImported: (appName: string, importId: number) => void
}

export default function ImportProjectModal({ onClose, onImported }: ImportProjectModalProps) {
  const [source, setSource] = useState<ImportSource>('url')
  const [githubConnected, setGithubConnected] = useState<boolean | null>(null)
  const [gitlabConnected, setGitlabConnected] = useState<boolean | null>(null)
  const [url, setUrl] = useState('')
  const [appTitle, setAppTitle] = useState('')
  const [appTitleEdited, setAppTitleEdited] = useState(false)
  const [slugInput, setSlugInput] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [nameCheck, setNameCheck] = useState<NameCheckResult | null>(null)
  const [checkingName, setCheckingName] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [envText, setEnvText] = useState('')
  const inFlightRef = useRef(false)
  const appName = finalizeAppSlug(slugInput)
  const hasAppTitle = appTitle.trim().length > 0

  // Auto-extract name from URL when URL changes (only if user hasn't manually edited)
  useEffect(() => {
    if (url && isValidImportURL(url)) {
      if (!appTitleEdited) {
        setAppTitle(extractDisplayName(url))
      }
      if (!slugEdited) {
        setSlugInput(extractRepoName(url))
      }
    }
  }, [url, appTitleEdited, slugEdited])

  useEffect(() => {
    if (!slugEdited) {
      setSlugInput(appSlugFromName(appTitle))
    }
  }, [appTitle, slugEdited])

  useEffect(() => {
    let cancelled = false
    if (!isValidAppSlug(appName)) {
      setNameCheck(null)
      setCheckingName(false)
      return
    }

    setCheckingName(true)
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkAppName(appName)
        if (!cancelled) {
          setNameCheck(result)
        }
      } catch {
        if (!cancelled) {
          setNameCheck(null)
        }
      } finally {
        if (!cancelled) {
          setCheckingName(false)
        }
      }
    }, 250)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [appName])

  // Check provider connections on mount
  useEffect(() => {
    let cancelled = false

    Promise.allSettled([
      getGitHubLinkStatus(),
      getGitLabLinkStatus(),
    ]).then(([githubResult, gitlabResult]) => {
      if (cancelled) return

      setGithubConnected(
        githubResult.status === 'fulfilled' ? githubResult.value.linked : false
      )
      setGitlabConnected(
        gitlabResult.status === 'fulfilled' ? gitlabResult.value.linked : false
      )
    })

    return () => {
      cancelled = true
    }
  }, [])

  // Best-effort: write pasted .env secrets into the new app before its first
  // deploy. The import has already started by the time this runs, so a failure
  // here must not block the flow — the user can still add secrets from App
  // Details. Values go straight to the encrypted store; never logged here.
  async function applyEnvSecrets(appNameToUse: string) {
    const content = envText.trim()
    if (!content) return
    try {
      await importEnvSecrets(appNameToUse, { content })
    } catch {
      // Non-fatal — secrets can be added later from App Details.
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValidImportURL(url) || !hasAppTitle || !isValidAppSlug(appName) || (nameCheck && !nameCheck.available)) return
    if (inFlightRef.current) return
    inFlightRef.current = true

    setSubmitting(true)
    setError(null)

    try {
      const result = await createImportFromURL({
        url,
        name: appName,
        display_name: appTitle || undefined,
      })
      await applyEnvSecrets(result.app_name)
      onImported(result.app_name, result.import_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start import')
    } finally {
      setSubmitting(false)
      inFlightRef.current = false
    }
  }

  async function handleGitHubImport(repo: GitHubRepo, branch: string, options?: { name?: string; display_name?: string }) {
    if (inFlightRef.current) return
    inFlightRef.current = true
    setSubmitting(true)
    setError(null)

    try {
      const result = await createImportFromGitHub({
        repo_full_name: repo.full_name,
        branch,
        name: options?.name || undefined,
        display_name: options?.display_name || undefined,
      })
      await applyEnvSecrets(result.app_name)
      onImported(result.app_name, result.import_id)
      setUrl(`https://github.com/${repo.full_name}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start import')
    } finally {
      setSubmitting(false)
      inFlightRef.current = false
    }
  }

  async function handleGitLabImport(repo: GitLabRepo, branch: string, options?: { name?: string; display_name?: string }) {
    if (inFlightRef.current) return
    inFlightRef.current = true
    setSubmitting(true)
    setError(null)

    try {
      const result = await createImportFromGitLab({
        project_id: repo.id,
        repo_full_name: repo.full_name,
        path_with_namespace: repo.path_with_namespace,
        branch,
        name: options?.name || undefined,
        display_name: options?.display_name || undefined,
        instance_url: repo.instance_url,
        web_url: repo.web_url,
      })
      await applyEnvSecrets(result.app_name)
      onImported(result.app_name, result.import_id)
      const repoUrl = repo.web_url
        || (repo.instance_url
          ? `${repo.instance_url.replace(/\/$/, '')}/${repo.path_with_namespace}`
          : '')
      if (repoUrl) {
        setUrl(repoUrl)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start import')
    } finally {
      setSubmitting(false)
      inFlightRef.current = false
    }
  }

  const urlValid = isValidImportURL(url)
  const nameValid = isValidAppSlug(appName)
  const canSubmit = urlValid && hasAppTitle && nameValid && !submitting && !(nameCheck && !nameCheck.available)

  // Shared across all import sources (url / github / gitlab). The value is held
  // in component state and applied by applyEnvSecrets() in every submit handler,
  // so it must be rendered in each tab — not just the URL form.
  const envSecretsField = (
    <details className="rounded-lg border border-border">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium select-none">
        Environment secrets <span className="text-muted-foreground font-normal">(optional)</span>
      </summary>
      <div className="px-3 pb-3">
        <textarea
          value={envText}
          onChange={(e) => setEnvText(e.target.value)}
          rows={6}
          spellCheck={false}
          autoComplete="off"
          placeholder={'# Paste your .env here\nDB_URI=postgresql://...\nOPENAI_API_KEY=sk-...'}
          className="w-full px-3 py-2 rounded-lg border border-border bg-background font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <p className="text-xs text-muted-foreground mt-1.5">
          Stored encrypted as app secrets before the first deploy. Platform-managed keys
          (e.g. <code className="bg-secondary px-1 rounded">DATABASE_URL</code>) are ignored.
          Values are never logged.
        </p>
      </div>
    </details>
  )

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-6">Import Project</h2>
          {/* Source selector tabs */}
          <div className="flex gap-2 mb-5">
            <button
              type="button"
              onClick={() => setSource('url')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                source === 'url'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              URL
            </button>
            <button
              type="button"
              onClick={() => setSource('github')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                source === 'github'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <Github className="w-4 h-4" />
              GitHub
              {githubConnected === true && (
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setSource('gitlab')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                source === 'gitlab'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <Gitlab className="w-4 h-4" />
              GitLab
              {gitlabConnected === true && (
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setSource('upload')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                source === 'upload'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <FolderUp className="w-4 h-4" />
              Upload
            </button>
          </div>

          {/* URL import form */}
          {source === 'url' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* URL Input */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium mb-1.5">
                  Repository URL *
                  <span title="Enter a public GitHub repository URL. Private repos require connecting your GitHub account in Settings">
                    <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </span>
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://github.com/owner/repo"
                  required
                  autoFocus
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  {url && !urlValid ? (
                    <span className="text-destructive">
                      URL must be HTTPS and from GitHub, GitLab, or Bitbucket
                    </span>
                  ) : (
                    'Paste a public repository URL from GitHub, GitLab, or Bitbucket'
                  )}
                </p>
              </div>

              {/* App Name Input */}
              <div>
                <label className="block text-sm font-medium mb-1.5">App Name *</label>
                <input
                  type="text"
                  value={appTitle}
                  onChange={(e) => {
                    setAppTitle(e.target.value)
                    setAppTitleEdited(true)
                  }}
                  placeholder="My Project"
                  required
                  maxLength={60}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  The human-readable name shown in the dashboard.
                </p>
              </div>

              {/* URL Slug Input */}
              <div>
                <label className="block text-sm font-medium mb-1.5">URL Slug *</label>
                <input
                  type="text"
                  value={slugInput}
                  onChange={(e) => {
                    setSlugInput(normalizeAppSlugInput(e.target.value))
                    setSlugEdited(true)
                  }}
                  placeholder="my-project"
                  required
                  minLength={3}
                  maxLength={50}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  {!appName ? 'Based on the app name by default. You can edit it.' : !nameValid ? (
                    <span className="text-destructive">
                      3-50 characters, starts with a letter, lowercase letters, numbers, and hyphens only
                    </span>
                  ) : checkingName ? (
                    'Checking availability...'
                  ) : nameCheck && !nameCheck.available ? (
                    <span className="text-amber-600">
                      Taken. Suggested: <code className="bg-secondary px-1 rounded">{nameCheck.suggestion || `${appName}-2`}</code>
                      {nameCheck.suggestion && (
                        <>
                          {' '}
                          <button
                            type="button"
                            onClick={() => {
                              setSlugInput(normalizeAppSlugInput(nameCheck.suggestion || ''))
                              setSlugEdited(true)
                            }}
                            className="underline underline-offset-2 hover:text-amber-700"
                          >
                            Use suggestion
                          </button>
                        </>
                      )}
                    </span>
                  ) : (
                    <>Your app will be at <code className="bg-secondary px-1 rounded">/a/{appName}/</code></>
                  )}
                </p>
              </div>



              {/* Optional: paste a .env to seed app secrets before first deploy */}
              {envSecretsField}

              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting import...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Import
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* GitHub repo browser */}
          {source === 'github' && (
            <>
              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
                  {error}
                </div>
              )}
              {/* Paste .env before picking a repo — applied on import (handleGitHubImport). */}
              <div className="mb-4">{envSecretsField}</div>
              <GitHubRepoBrowser
                onSelect={(repo, branch, options) => handleGitHubImport(repo, branch, options)}
                onCancel={onClose}
                importing={submitting}
              />
            </>
          )}

          {/* GitLab repo browser */}
          {source === 'gitlab' && (
            <>
              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
                  {error}
                </div>
              )}
              {/* Paste .env before picking a repo — applied on import (handleGitLabImport). */}
              <div className="mb-4">{envSecretsField}</div>
              <GitLabRepoBrowser
                onSelect={(repo, branch, options) => handleGitLabImport(repo, branch, options)}
                onCancel={onClose}
                importing={submitting}
              />
            </>
          )}

          {/* Upload (folder drag-and-drop) */}
          {source === 'upload' && (
            <>
              {/* Paste .env before uploading — applied once the upload creates the app. */}
              <div className="mb-4">{envSecretsField}</div>
              <FolderDropZone
                onImported={async (appName, importId) => {
                  await applyEnvSecrets(appName)
                  onImported(appName, importId)
                }}
                onCancel={onClose}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
